import type { Room } from './types'

export function getRoomBookingCustomerIds(room: Room): number[] {
  return room.participantsInfo?.map(p => p.bookingCustomerId) ?? []
}

export function getUnassignedParticipantIds(
  rooms: Record<string, Room>,
  assignments: Record<string, number[]>,
): number[] {
  const originallyAssignedIds = Object.values(rooms).flatMap(getRoomBookingCustomerIds)
  const assignedIds = new Set(Object.values(assignments).flat())

  return originallyAssignedIds.filter(id => !assignedIds.has(id))
}

function initializeRoomAssignments(
  rooms: Record<string, Room>,
  currentAssignments?: Record<string, number[]>,
): Record<string, number[]> {
  const assignments: Record<string, number[]> = {}
  for (const roomId of Object.keys(rooms)) {
    assignments[roomId] = currentAssignments?.[roomId] ?? getRoomBookingCustomerIds(rooms[roomId])
  }
  return assignments
}

function removeParticipantsFromOtherRooms(
  assignments: Record<string, number[]>,
  targetRoomId: string,
  addedParticipants: number[],
): void {
  for (const addedId of addedParticipants) {
    for (const roomId of Object.keys(assignments)) {
      if (roomId !== targetRoomId && assignments[roomId].includes(addedId)) {
        assignments[roomId] = assignments[roomId].filter(id => id !== addedId)
      }
    }
  }
}

export function computeRoomSwapAssignments(
  rooms: Record<string, Room>,
  targetRoomId: string,
  newTargetBookingCustomerIds: number[],
  currentAssignments?: Record<string, number[]>,
): Record<string, number[]> {
  const assignments = initializeRoomAssignments(rooms, currentAssignments)
  const currentTargetIds = assignments[targetRoomId]

  if (!currentTargetIds) {
    return assignments
  }

  const addedParticipants = newTargetBookingCustomerIds.filter(id => !currentTargetIds.includes(id))
  const hasRemovedParticipants = currentTargetIds.some(id => !newTargetBookingCustomerIds.includes(id))

  if (addedParticipants.length === 0 && !hasRemovedParticipants) {
    return assignments
  }

  assignments[targetRoomId] = [...newTargetBookingCustomerIds]

  removeParticipantsFromOtherRooms(
    assignments,
    targetRoomId,
    addedParticipants,
  )

  return assignments
}
